/**
 * applyReorderToItemConfiguration — pure projection of a PreparedReorder's
 * `field_resolutions` into CatalogueItemPage's `ConfigState.values`/`custom`
 * pockets, per the three-tier ladder (brief §16):
 *
 * 1. preset   — the option chip is pre-selected, indistinguishable from a
 *    manual pick.
 * 2. custom   — the custom-value control is pre-populated (reuses #1548's
 *    controls as-is; `choice_or_custom` fields also need the "Other…"
 *    sentinel set so the control renders open).
 * 3. unresolved — left out of both pockets entirely. `ChipRow` already
 *    renders "no selection" for an absent key (`state.values[key] ?? ""`),
 *    so this needs no special-case UI beyond the "was: X" chip the caller
 *    renders from `field_resolutions` directly — see `unresolvedFieldKeys`.
 *
 * A `fixed`-mode field is skipped entirely, matching `initialState()`'s own
 * treatment: fixed fields are never part of `ConfigState` — they render from
 * `field.fixedValue` and the backend applies the locked option regardless of
 * `configuration`'s contents.
 *
 * Kept in shared-core (frontend-architecture.md: non-visual state-shaping
 * logic lands here, not in the page) so it is testable without rendering
 * anything.
 */

import { CUSTOM_OPTION_SENTINEL } from "./catalogue-configuration";
import type { CatalogueField } from "../data/catalogue-types";
import type { ReorderFieldResolution } from "../data/api-types";

/**
 * The dictionary field a numbered item's start number is typed into
 * (`dict-ncr-set`, `dict-ncr-pad`, `dict-ncr-book`; the backend's
 * `START_NUMBER_FIELD_KEY`). The next-start proposal names no field to write
 * into, so the "Apply {next_start}" button targets this one.
 */
export const START_NUMBER_FIELD_KEY = "StartNumber";

export interface AppliedReorderConfiguration {
  /** Preset chip selections, keyed by field key — merges straight into
   *  `ConfigState.values`. */
  values: Record<string, string>;
  /** Custom-value entries, keyed by field key — merges straight into
   *  `ConfigState.custom`. */
  custom: Record<string, string>;
  /** Field keys left for the CSR to pick (tier `"unresolved"`) — drives the
   *  "was: X" chip and blocks price-preview until empty. */
  unresolvedFieldKeys: string[];
}

export function applyReorderToItemConfiguration(
  fieldResolutions: readonly ReorderFieldResolution[],
  fields: readonly CatalogueField[],
): AppliedReorderConfiguration {
  const values: Record<string, string> = {};
  const custom: Record<string, string> = {};
  const unresolvedFieldKeys: string[] = [];
  const fieldsByKey = new Map(fields.map((f) => [f.key, f]));

  for (const resolution of fieldResolutions) {
    const field = fieldsByKey.get(resolution.field_key);
    // The winning item no longer declares this field (spec drift between
    // when prepare_reorder ran and now) — nowhere to land it, so skip
    // rather than writing an orphaned key into state.
    if (!field || field.mode === "fixed") continue;

    if (resolution.tier === "preset" && resolution.resolved_option_code) {
      values[resolution.field_key] = resolution.resolved_option_code;
    } else if (
      resolution.tier === "custom" &&
      resolution.resolved_custom_value != null
    ) {
      if (field.mode === "choice_or_custom") {
        // Opens the "Other…" control so the pre-populated custom value is
        // visible rather than hidden behind an unselected chip row.
        values[resolution.field_key] = CUSTOM_OPTION_SENTINEL;
      }
      custom[resolution.field_key] = resolution.resolved_custom_value;
    } else if (resolution.tier === "unresolved") {
      unresolvedFieldKeys.push(resolution.field_key);
    }
  }

  return { values, custom, unresolvedFieldKeys };
}

/** Lookup map from field key to its resolution, for rendering the per-field
 * tier chip ("was: X" on an unresolved field, a subtle badge on a resolved
 * one) next to each field's control. */
export function reorderFieldResolutionsByKey(
  fieldResolutions: readonly ReorderFieldResolution[],
): Map<string, ReorderFieldResolution> {
  return new Map(fieldResolutions.map((r) => [r.field_key, r]));
}
